import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import ElementSettingsContext from '../helpers/element-settings-context';
import mappingElementIdsToTargets from '../helpers/mapping-element-ids-to-targets';
import getBatchValues from '../helpers/get-batch-values';
import createBatch from '../helpers/create-batch';
import modifyBatchChanges from '../helpers/modify-batch-changes';
import ToggleableSection from './toggleable-section';

function ToggleableSectionControl({
  id,
  elementIds,
  targets,
  title,
  popover = null,
  computeFallbackValue = null,
  onChange,
  children,
}) {
  const { resolvedState } = useContext(ElementSettingsContext);

  const elementIdsToTargets = mappingElementIdsToTargets(elementIds, targets);
  const flattenedValues = getBatchValues(resolvedState, { elementIds, targets }).flat();
  const areAllValuesBooleans = flattenedValues.every((value) => typeof value === 'boolean');
  const isToggleEnabled = areAllValuesBooleans
    ? flattenedValues.every((value) => value !== false)
    : flattenedValues.some((value) => value !== false);

  const handleChange = useCallback(
    (value) => {
      const batch = modifyBatchChanges({
        batch: createBatch({
          elementIds,
          targets,
          value,
        }),
        onFilterCallback: ({ elementId, target }) =>
          elementIdsToTargets[elementId].includes(target),
        onComputeValueCallback: ({ elementId, target, value: val }) =>
          val && computeFallbackValue ? computeFallbackValue({ elementId, target }) : val,
      });

      onChange({ batch, controlId: id, value });
    },
    [id, elementIds, targets, onChange, computeFallbackValue, elementIdsToTargets]
  );

  return (
    <ToggleableSection
      value={isToggleEnabled}
      title={title}
      popover={popover}
      onChange={handleChange}
    >
      {children}
    </ToggleableSection>
  );
}

ToggleableSectionControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  popover: PropTypes.shape({
    title: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
  }),
  computeFallbackValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default ToggleableSectionControl;
